import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useAllFakturowniaInvoices, useStudentAlerts } from 'core/hooks';
import {
  canShowNoIssues,
  IStudentAlert,
  IStudentAlertTarget,
  StudentAlertId,
} from 'core/utils/studentAlerts';

import { IStudent } from './types';
import { StudentColors } from './studentColors';

interface IAlertsPanelProps {
  student: IStudent;
  /** Atomic alert navigation (set tab + filter + scroll), Req 6. */
  onNavigate: (target: IStudentAlertTarget) => void;
}

/** Per-category severity token: overdue/lost are danger, the rest warn. */
const SEVERITY: Record<StudentAlertId, 'danger' | 'warn'> = {
  overdueContributions: 'danger',
  outstandingInvoices: 'warn',
  orphanInvoices: 'warn',
  orphanPayments: 'warn',
  unsettledPayables: 'warn',
  pandadocMissingOrLost: 'danger',
};

/**
 * «Needs attention» panel for the Student card (Area 2, Req 5–6).
 *
 * Derives the six alert categories from the already-loaded `useStudentDetail`
 * data plus a fresh orphan-invoice lookup (the same set `OrphanInvoicesBlock`
 * uses, Req 5.5). Shows only non-zero categories with their localized label +
 * count; a loading state while any source loads; a localized «no issues» state
 * only when everything loaded clean; and an «unevaluable» note for failed
 * sources, visually distinct from «no issues» (Req 5.8). Selecting an alert
 * runs the atomic deep-link navigation (Req 6). Colors from `StudentColors`.
 */
export const AlertsPanel = memo(
  ({ student, onNavigate }: IAlertsPanelProps) => {
    const { t } = useTranslation();

    const {
      data: invoices,
      fetching,
      loading,
      error,
    } = useAllFakturowniaInvoices({
      studentId: student.id,
      direction: 'issued',
    });

    const orphanInvoices = useMemo(
      () => ({
        count: invoices.filter((r) => !r.payment).length,
        loading: loading || fetching,
        failed: Boolean(error),
      }),
      [invoices, loading, fetching, error],
    );

    const state = useStudentAlerts(student, orphanInvoices);

    // While anything is loading, show the loading row and withhold «no issues».
    if (state.anyLoading && state.alerts.length === 0) {
      return (
        <Panel aria-busy="true">
          <Title>{t('pages.studentDetail.alerts.title')}</Title>
          <Muted>{t('pages.studentDetail.alerts.loading')}</Muted>
        </Panel>
      );
    }

    const showNoIssues = canShowNoIssues(state);

    return (
      <Panel>
        <Title>{t('pages.studentDetail.alerts.title')}</Title>

        {state.alerts.length > 0 ? (
          <List>
            {state.alerts.map((alert: IStudentAlert) => (
              <AlertChip
                key={alert.id}
                type="button"
                severity={SEVERITY[alert.id]}
                onClick={() => onNavigate(alert.target)}
              >
                <ChipLabel>
                  {t(`pages.studentDetail.alerts.categories.${alert.id}`)}
                </ChipLabel>
                <ChipCount severity={SEVERITY[alert.id]}>
                  {alert.count}
                </ChipCount>
              </AlertChip>
            ))}
          </List>
        ) : null}

        {showNoIssues ? (
          <NoIssues>{t('pages.studentDetail.alerts.noIssues')}</NoIssues>
        ) : null}

        {state.unevaluable.length > 0 ? (
          <Unevaluable role="status">
            {t('pages.studentDetail.alerts.unevaluable', {
              count: state.unevaluable.length,
            })}
          </Unevaluable>
        ) : null}
      </Panel>
    );
  },
);

const Panel = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px solid ${StudentColors.border};
  border-radius: 10px;
  background: ${StudentColors.surface};
`;

const Title = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${StudentColors.textMuted};
  margin-bottom: 10px;
`;

const List = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const AlertChip = styled.button<{ severity: 'danger' | 'warn' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid
    ${({ severity }) =>
      severity === 'danger' ? StudentColors.danger : StudentColors.warn};
  background: ${StudentColors.surface};
  border-radius: 999px;
  padding: 5px 8px 5px 12px;
  font: inherit;
  font-size: 13px;
  color: ${StudentColors.textSecondary};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${StudentColors.accent};
    outline-offset: 2px;
  }
`;

const ChipLabel = styled.span`
  font-weight: 600;
`;

const ChipCount = styled.span<{ severity: 'danger' | 'warn' }>`
  min-width: 20px;
  text-align: center;
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 12px;
  font-weight: 700;
  color: ${StudentColors.surface};
  background: ${({ severity }) =>
    severity === 'danger' ? StudentColors.danger : StudentColors.warn};
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${StudentColors.textMuted};
`;

const NoIssues = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${StudentColors.success};
`;

const Unevaluable = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${StudentColors.warn};
`;
